import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Select,
  Card,
  Space,
  Alert,
  Divider,
  InputNumber,
  Table,
  Modal,
  Empty,
  Tooltip,
  Badge,
  Typography,
  Collapse,
  DatePicker,
  message,
  Spin,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, FilterOutlined, AppstoreOutlined, EyeOutlined } from "@ant-design/icons";
import { withRouter } from "react-router";
import moment from "moment";
import { createEmailSequence } from "@api/email/emailSequence";
import { getEmailCampaignMetadata } from "@api/email/emailCampaign";
import { getHtmlTemplateById } from "@api/email/htmlTemplate";
import { getActiveUserEmails } from "@api/email/users/userEmail";
import { MailOutlined, UserOutlined } from "@ant-design/icons";
import { SuccessNotification, ErrorNotification } from "@components";
import SequenceTemplateGallery from "@views/superAdmin/emails/emailSequences/components/SequenceTemplateGallery";

const { Option } = Select;
const { Text } = Typography;

// Initial state for the sequence
const initialState = {
  name: "",
  description: "",
  type: "onboarding",
  trigger: {
    eventType: "signup",
    delay: {
      value: 0,
      unit: "minutes",
    },
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any",
      lastActivityDaysAgo: {},
      totalGenerations: {},
      creditsUsed: {},
      creditsRemaining: {},
    },
    demographic: {
      signupDateRange: {},
      daysSinceSignup: {},
      subscriptionTier: ["any"],
      subscriptionStatus: ["any"],
      accountType: ["any"],
      userRole: ["any"],
    },
  },
  emails: [],
  active: true,
};

// Constants for the form
const SEQUENCE_TYPES = [
  { value: "onboarding", label: "Onboarding" },
  { value: "engagement", label: "Engagement" },
  { value: "milestone", label: "Milestone" },
  { value: "retention", label: "Retention" },
  { value: "inactivity", label: "Inactivity" },
];

const TRIGGER_TYPES = [
  { value: "signup", label: "User Signup" },
  { value: "firstGeneration", label: "First AI Generation" },
  { value: "creditsLow", label: "Credits Running Low" },
  { value: "inactivity", label: "User Inactivity" },
  { value: "timeAfterSignup", label: "Time After Signup" },
];

const TIME_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

// Segmentation options
const USAGE_PATTERNS = [
  { value: "any", label: "Any" },
  { value: "active", label: "Active (50+ generations/month)" },
  { value: "moderate", label: "Moderate (10-49 generations/month)" },
  { value: "inactive", label: "Inactive (<10 generations/month)" },
];

const ENGAGEMENT_LEVELS = [
  { value: "any", label: "Any" },
  { value: "high", label: "High (Active in last 7 days)" },
  { value: "medium", label: "Medium (Active in last 30 days)" },
  { value: "low", label: "Low (Inactive 30+ days)" },
];

const SUBSCRIPTION_TIERS = [
  { value: "any", label: "Any" },
  { value: "Free", label: "Free" },
  { value: "Starter", label: "Starter" },
  { value: "Growth", label: "Growth" },
  { value: "Pro", label: "Pro" },
  { value: "Enterprise", label: "Enterprise" },
];

const SUBSCRIPTION_STATUS = [
  { value: "any", label: "Any" },
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "canceled", label: "Canceled" },
  { value: "past_due", label: "Past Due" },
];

const ACCOUNT_TYPES = [
  { value: "any", label: "Any" },
  { value: "local", label: "Local" },
  { value: "google", label: "Google" },
];

const USER_ROLES = [
  { value: "any", label: "Any" },
  { value: "admin", label: "Admin" },
  { value: "agent", label: "Agent" },
  { value: "superadmin", label: "Super Admin" },
  { value: "marketing", label: "Marketing" },
];

const EmailSequenceCreateForm = ({ history }) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editingEmail, setEditingEmail] = useState(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [availableEmails, setAvailableEmails] = useState({});
  const [templateGalleryVisible, setTemplateGalleryVisible] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);

  // Auto-selected HTML template tracking for each email
  const [autoSelectedTemplates, setAutoSelectedTemplates] = useState([]);

  // HTML Template preview
  const [templatePreviewVisible, setTemplatePreviewVisible] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState(null);

  useEffect(() => {
    fetchAvailableEmails();
  }, []);

  const fetchAvailableEmails = async () => {
    try {
      const response = await getActiveUserEmails();
      const emailsByDomain = response.data.reduce((acc, email) => {
        const domain = email.email.split("@")[1];
        if (!acc[domain]) {
          acc[domain] = [];
        }
        acc[domain].push(email);
        return acc;
      }, {});
      setAvailableEmails(emailsByDomain);
    } catch (error) {
      console.error("Error fetching available emails:", error);
      ErrorNotification("Failed to fetch available emails");
    }
  };

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Function to fetch html templates
  const fetchTemplates = async () => {
    try {
      const response = await getEmailCampaignMetadata();
      setTemplates(response.data.htmlTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      ErrorNotification("Failed to fetch html templates");
    }
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle trigger changes
  const handleTriggerChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        [field]: value,
      },
    }));
  };

  // Handle delay changes
  const handleDelayChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        delay: {
          ...prev.trigger.delay,
          [field]: value,
        },
      },
    }));
  };

  // Handle segmentation changes
  const handleSegmentationChange = (category, field, value) => {
    setValues((prev) => ({
      ...prev,
      segmentation: {
        ...prev.segmentation,
        [category]: {
          ...prev.segmentation[category],
          [field]: value,
        },
      },
    }));
  };

  // Handle range segmentation changes
  const handleRangeChange = (category, field, subField, value) => {
    setValues((prev) => ({
      ...prev,
      segmentation: {
        ...prev.segmentation,
        [category]: {
          ...prev.segmentation[category],
          [field]: {
            ...prev.segmentation[category][field],
            [subField]: value,
          },
        },
      },
    }));
  };

  // Email handlers
  const handleAddEmail = () => {
    setEditingEmail(null);
    setEmailModalVisible(true);
  };

  const handleEditEmail = (email, index) => {
    setEditingEmail({ ...email, index });
    setEmailModalVisible(true);
  };

  const handleDeleteEmail = (index) => {
    setValues((prev) => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index),
    }));
  };

  const handleSaveEmail = (emailData) => {
    setValues((prev) => {
      const newEmails = [...prev.emails];
      if (editingEmail?.index !== undefined) {
        newEmails[editingEmail.index] = emailData;
      } else {
        newEmails.push(emailData);
      }
      return { ...prev, emails: newEmails };
    });
    setEmailModalVisible(false);
  };

  // Handle HTML template preview
  const handleTemplatePreview = async (templateId) => {
    if (!templateId) {
      message.warning("Please select an HTML template first");
      return;
    }

    setTemplatePreviewLoading(true);
    setTemplatePreviewVisible(true);

    try {
      const res = await getHtmlTemplateById(templateId);
      setTemplatePreviewData(res.data);
    } catch (error) {
      console.error("Error fetching template for preview", error);
      message.error("Failed to load template preview");
      setTemplatePreviewVisible(false);
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  /**
   * Find matching HTML template for a sequence email
   * Uses multiple matching strategies with priority:
   * 1. Exact templateId match
   * 2. Normalized templateId match (camelCase to kebab-case)
   * 3. Category/type based matching
   * 4. Name-based fuzzy matching
   * 5. Fallback to default
   */
  const findMatchingHtmlTemplate = (suggestedTemplateId, sequenceType) => {
    if (!templates || templates.length === 0 || !suggestedTemplateId) {
      return { template: null, matchType: null, matchReason: null };
    }

    // 1. Try exact match by templateId
    const exactMatch = templates.find((t) => t.templateId === suggestedTemplateId);
    if (exactMatch) {
      return {
        template: exactMatch,
        matchType: "exact",
        matchReason: `Perfect match for "${suggestedTemplateId}"`,
      };
    }

    // 2. Try normalized match (camelCase to kebab-case)
    const normalizedId = suggestedTemplateId
      .replace(/([A-Z])/g, "-$1")
      .toLowerCase()
      .replace(/^-/, "");
    const normalizedMatch = templates.find((t) => t.templateId === normalizedId);
    if (normalizedMatch) {
      return {
        template: normalizedMatch,
        matchType: "normalized",
        matchReason: `Matched "${normalizedId}"`,
      };
    }

    // 3. Try partial templateId match
    const partialMatch = templates.find((t) =>
      t.templateId?.toLowerCase().includes(suggestedTemplateId.toLowerCase()) ||
      suggestedTemplateId.toLowerCase().includes(t.templateId?.toLowerCase() || "")
    );
    if (partialMatch) {
      return {
        template: partialMatch,
        matchType: "partial",
        matchReason: `Partial match for "${suggestedTemplateId}"`,
      };
    }

    // 4. Map sequence type to template category
    const typeMapping = {
      onboarding: ["sequences", "onboarding", "marketing"],
      retention: ["sequences", "marketing", "transactional"],
      engagement: ["sequences", "marketing", "product"],
      milestone: ["marketing", "sequences", "events"],
      inactivity: ["sequences", "marketing"],
      revenue: ["marketing", "product", "sequences"],
      growth: ["marketing", "sequences"],
      education: ["marketing", "sequences"],
      lifecycle: ["sequences", "onboarding", "marketing"],
    };

    const mappedCategories = typeMapping[sequenceType] || ["sequences", "marketing"];

    for (const category of mappedCategories) {
      const categoryMatch = templates.find((t) => t.category === category);
      if (categoryMatch) {
        return {
          template: categoryMatch,
          matchType: "category",
          matchReason: `Best match for ${sequenceType} sequences`,
        };
      }
    }

    // 5. Try name-based fuzzy matching
    const searchTerms = suggestedTemplateId
      .replace(/([A-Z])/g, " $1")
      .toLowerCase()
      .trim()
      .split(/[\s-_]+/);

    const nameMatch = templates.find((t) =>
      searchTerms.some((term) => t.name?.toLowerCase().includes(term))
    );
    if (nameMatch) {
      return {
        template: nameMatch,
        matchType: "name",
        matchReason: `Name match for "${suggestedTemplateId}"`,
      };
    }

    // 6. Fallback to default or first template
    const defaultTemplate = templates.find((t) => t.isDefault) || templates[0];
    return {
      template: defaultTemplate,
      matchType: "fallback",
      matchReason: "Default template - feel free to change",
    };
  };

  // Handle sequence template selection from gallery
  const handleTemplateSelect = (template) => {
    // Track auto-selected templates for each email
    const autoSelections = [];

    // Convert template emails to form emails format with smart matching
    const formEmails = template.emails.map((email, index) => {
      const { template: matchedTemplate, matchType, matchReason } = findMatchingHtmlTemplate(
        email.suggestedTemplateId,
        template.type
      );

      // Track the auto-selection
      autoSelections.push({
        emailIndex: index,
        emailName: email.name,
        templateId: matchedTemplate?._id,
        templateName: matchedTemplate?.name,
        matchType,
        matchReason,
        suggestedId: email.suggestedTemplateId,
      });

      return {
        subject: email.subject,
        templateId: matchedTemplate?._id || null,
        delayAfterPrevious: email.delayAfterPrevious,
        emailsToUse: [],
        fromName: "",
        replyTo: "",
        variables: email.variables?.map((v) => ({
          key: v.key,
          value: v.defaultValue,
        })) || [],
      };
    });

    // Build segmentation - convert arrays to expected format
    const buildSegmentation = (templateSeg) => {
      const seg = {
        behavior: {
          usagePattern: templateSeg.behavior.usagePattern || "any",
          engagementLevel: templateSeg.behavior.engagementLevel || "any",
          lastActivityDaysAgo: {},
          totalGenerations: {},
          creditsUsed: {},
          creditsRemaining: {},
        },
        demographic: {
          signupDateRange: {},
          daysSinceSignup: {},
          subscriptionTier: templateSeg.demographic.subscriptionTier?.length > 0
            ? templateSeg.demographic.subscriptionTier
            : ["any"],
          subscriptionStatus: templateSeg.demographic.subscriptionStatus?.length > 0
            ? templateSeg.demographic.subscriptionStatus
            : ["any"],
          accountType: ["any"],
          userRole: ["any"],
        },
      };
      return seg;
    };

    // Apply template to form values
    setValues({
      name: template.name,
      description: template.description,
      type: template.type,
      trigger: {
        eventType: template.trigger.eventType,
        delay: template.trigger.delay,
      },
      segmentation: buildSegmentation(template.segmentation),
      emails: formEmails,
      active: true,
    });

    // Store auto-selection info
    setAutoSelectedTemplates(autoSelections);
    setSelectedTemplateName(template.name);

    // Show success message with template matching summary
    const matchedCount = autoSelections.filter((s) => s.templateId).length;
    const totalCount = autoSelections.length;
    const templateMsg = matchedCount > 0
      ? ` ${matchedCount}/${totalCount} HTML templates auto-selected.`
      : "";
    message.success(`Template "${template.name}" applied!${templateMsg} Review and customize the settings.`);
  };

  // Form submission
  const handleSubmit = async () => {
    // Validate form
    if (!values.name || !values.type || values.emails.length === 0) {
      return ErrorNotification("Please fill all required fields");
    }

    setLoading(true);
    try {
      await createEmailSequence(values);
      SuccessNotification("Email sequence created successfully");
      history.goBack();
    } catch (error) {
      console.error("Error creating sequence:", error);
      ErrorNotification("Failed to create email sequence");
    } finally {
      setLoading(false);
    }
  };

  // Email form component
  const EmailForm = ({ email, onSave, onCancel, emailModalVisible }) => {
    const [form] = Form.useForm();
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [variableModalVisible, setVariableModalVisible] = useState(false);
    const [editingVariable, setEditingVariable] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Initialize form values when email prop changes or modal opens
    useEffect(() => {
      if (emailModalVisible && email) {
        // Use setTimeout to ensure form is mounted before setting values
        setTimeout(() => {
          const initialValues = {
            ...email,
            emailsToUse:
              email.emailsToUse?.map((e) =>
                e.useAlias
                  ? `alias:${e.email}:${e.selectedAlias}`
                  : `email:${e.email}`
              ) || [],
            variables: email.variables || [],
          };
          form.setFieldsValue(initialValues);
          setSelectedEmails(initialValues.emailsToUse);
        }, 0);
      } else if (!emailModalVisible) {
        form.resetFields();
        setSelectedEmails([]);
      }
    }, [email, form, emailModalVisible]);

    const formatEmailsForSelect = () => {
      return Object.entries(availableEmails).map(([domain, emails]) => {
        const options = emails
          .map((email) => {
            const baseOption = {
              value: `email:${email._id}`,
              label: (
                <Space>
                  <MailOutlined />
                  <span>{email.name || email.email.split("@")[0]}</span>
                  <Text type="secondary">&lt;{email.email}&gt;</Text>
                </Space>
              ),
              email: email,
            };

            const aliasOptions = (email.aliases || []).map((alias) => ({
              value: `alias:${email._id}:${alias}`,
              label: (
                <Space style={{ paddingLeft: 24 }}>
                  <UserOutlined />
                  <span>{alias.split("@")[0]}</span>
                  <Text type="secondary">&lt;{alias}&gt;</Text>
                  <Badge
                    count="Alias"
                    style={{
                      backgroundColor: "#e6f4ff",
                      color: "#1890ff",
                      fontSize: "12px",
                      border: "1px solid #91caff",
                    }}
                  />
                </Space>
              ),
              parentEmail: email,
              isAlias: true,
            }));

            return [baseOption, ...aliasOptions];
          })
          .flat();

        return {
          label: (
            <Space>
              <span>Domain: {domain}</span>
              <Text type="secondary">
                ({emails.length} email{emails.length !== 1 ? "s" : ""})
              </Text>
            </Space>
          ),
          options,
        };
      });
    };

    const handleEmailSelection = (selectedValues) => {
      setSelectedEmails(selectedValues);
      form.setFieldsValue({
        emailsToUse: selectedValues,
      });
    };

    const getSelectedEmailDetails = () => {
      const details = {
        totalEmails: selectedEmails.length,
        baseEmails: 0,
        aliases: 0,
        domains: new Set(),
      };

      selectedEmails.forEach((value) => {
        const [type, emailId, alias] = value.split(":");
        if (type === "email") {
          details.baseEmails++;
          const email = Object.values(availableEmails)
            .flat()
            .find((e) => e._id === emailId);
          if (email) {
            details.domains.add(email.email.split("@")[1]);
          }
        } else {
          details.aliases++;
          details.domains.add(alias.split("@")[1]);
        }
      });

      return details;
    };

    const handleVariableSave = (values) => {
      try {
        const currentVariables = form.getFieldValue("variables") || [];
        if (editingVariable !== null) {
          const newVariables = [...currentVariables];
          newVariables[editingVariable] = values;
          form.setFieldsValue({ variables: newVariables });
        } else {
          form.setFieldsValue({
            variables: [...currentVariables, values],
          });
        }
        setVariableModalVisible(false);
        setEditingVariable(null);
      } catch (error) {
        console.error("Error saving variable:", error);
        message.error("Failed to save variable");
      }
    };

    const handleVariableDelete = (index) => {
      try {
        const currentVariables = form.getFieldValue("variables") || [];
        const newVariables = currentVariables.filter((_, i) => i !== index);
        form.setFieldsValue({ variables: newVariables });
      } catch (error) {
        console.error("Error deleting variable:", error);
        message.error("Failed to delete variable");
      }
    };

    const handleSubmit = async () => {
      try {
        setProcessing(true);
        const values = await form.validateFields();

        const formattedValues = {
          ...values,
          emailsToUse: selectedEmails.map((value) => {
            const [type, emailId, alias] = value.split(":");
            return {
              email: emailId,
              useAlias: type === "alias",
              selectedAlias: type === "alias" ? alias : null,
            };
          }),
          variables: values.variables || [],
        };

        onSave(formattedValues);
        form.resetFields();
        setSelectedEmails([]);
        setProcessing(false);
      } catch (error) {
        console.error("Form validation error:", error);
        setProcessing(false);
        if (error.errorFields) {
          // Form validation error
          message.error("Please fill in all required fields correctly");
        } else {
          // Other errors
          message.error("An error occurred while saving the email");
        }
      }
    };

    const selectedDetails = getSelectedEmailDetails();

    const VariableForm = ({ onSave, onCancel }) => {
      const [variableForm] = Form.useForm();
      const isEditing = editingVariable !== null;
      const currentVariables = form.getFieldValue("variables") || [];
      const editingValue = isEditing ? currentVariables[editingVariable] : null;

      useEffect(() => {
        if (editingValue) {
          variableForm.setFieldsValue(editingValue);
        } else {
          variableForm.resetFields();
        }
      }, [editingValue, variableForm]);

      const handleVariableSubmit = async () => {
        try {
          const values = await variableForm.validateFields();
          onSave(values);
          variableForm.resetFields();
        } catch (error) {
          console.error("Variable form validation error:", error);
        }
      };

      return (
        <Modal
          title={isEditing ? "Edit Variable" : "Add Variable"}
          visible={variableModalVisible}
          onOk={handleVariableSubmit}
          onCancel={onCancel}
          destroyOnClose
        >
          <Form form={variableForm} layout="vertical">
            <Form.Item
              name="key"
              label="Variable Key"
              rules={[
                { required: true, message: "Please enter a variable key" },
                {
                  pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                  message:
                    "Key must start with a letter and contain only letters, numbers, and underscores",
                },
              ]}
            >
              <Input placeholder="e.g. user_name" />
            </Form.Item>
            <Form.Item
              name="value"
              label="Default Value"
              rules={[
                { required: true, message: "Please enter a default value" },
              ]}
            >
              <Input placeholder="Default value" />
            </Form.Item>
          </Form>
        </Modal>
      );
    };

    return (
      <Modal
        title={email ? "Edit Email" : "Add Email"}
        visible={emailModalVisible}
        onOk={handleSubmit}
        onCancel={onCancel}
        confirmLoading={processing}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            variables: [],
            delayAfterPrevious: {
              value: 0,
              unit: "hours",
            },
          }}
        >
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: "Please enter email subject" }]}
          >
            <Input placeholder="Enter email subject" />
          </Form.Item>

          <Form.Item label="HTML Template" required>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item
                name="templateId"
                noStyle
                rules={[{ required: true, message: "Please select a template" }]}
              >
                <Select placeholder="Select email template" style={{ flex: 1 }}>
                  {templates.map((template) => (
                    <Option key={template._id} value={template._id}>
                      {template.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Tooltip title="Preview template">
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => handleTemplatePreview(form.getFieldValue("templateId"))}
                >
                  Preview
                </Button>
              </Tooltip>
            </Space.Compact>
          </Form.Item>

          <Form.Item
            name="emailsToUse"
            label="Sending Emails"
            required
            tooltip="Select email addresses and aliases to send this email from"
            rules={[
              { required: true, message: "Please select at least one email" },
            ]}
          >
            <Card>
              <Select
                mode="multiple"
                style={{ width: "100%", marginBottom: 16 }}
                placeholder="Select emails and aliases to send from..."
                value={selectedEmails}
                onChange={handleEmailSelection}
                optionLabelProp="label"
                optionFilterProp="label"
                options={formatEmailsForSelect()}
                listHeight={400}
                maxTagCount={5}
              />

              {selectedEmails.length > 0 && (
                <Card
                  size="small"
                  title="Selected Sending Configuration"
                  bordered={false}
                  style={{ background: "#f5f5f5" }}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Space>
                      <Badge
                        count={selectedDetails.totalEmails}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                      <Text>Total sending addresses selected</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.baseEmails}
                        style={{ backgroundColor: "#52c41a" }}
                      />
                      <Text>Primary email addresses</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.aliases}
                        style={{ backgroundColor: "#722ed1" }}
                      />
                      <Text>Email aliases</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.domains.size}
                        style={{ backgroundColor: "#faad14" }}
                      />
                      <Text>Unique sending domains</Text>
                    </Space>
                  </Space>
                </Card>
              )}
            </Card>
          </Form.Item>

          <Form.Item name="fromName" label="From Name">
            <Input placeholder="Enter sender name" />
          </Form.Item>

          <Form.Item name="replyTo" label="Reply To">
            <Input placeholder="Enter reply-to email" />
          </Form.Item>

          <Form.Item label="Delay After Previous Email" required>
            <Input.Group compact>
              <Form.Item
                name={["delayAfterPrevious", "value"]}
                noStyle
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} style={{ width: "60%" }} />
              </Form.Item>
              <Form.Item
                name={["delayAfterPrevious", "unit"]}
                noStyle
                rules={[{ required: true, message: "Required" }]}
              >
                <Select style={{ width: "40%" }}>
                  {TIME_UNITS.map((unit) => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item name="variables" label="Variables">
            <Card>
              <Button
                type="dashed"
                onClick={() => {
                  setEditingVariable(null);
                  setVariableModalVisible(true);
                }}
                block
                icon={<PlusOutlined />}
              >
                Add Variable
              </Button>

              <Table
                style={{ marginTop: 16 }}
                dataSource={form.getFieldValue("variables") || []}
                rowKey={(record, index) => index}
                columns={[
                  {
                    title: "Key",
                    dataIndex: "key",
                    key: "key",
                  },
                  {
                    title: "Default Value",
                    dataIndex: "value",
                    key: "value",
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, record, index) => (
                      <Space>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingVariable(index);
                            setVariableModalVisible(true);
                          }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleVariableDelete(index)}
                        />
                      </Space>
                    ),
                  },
                ]}
                pagination={false}
              />
            </Card>
          </Form.Item>
        </Form>

        <VariableForm
          onSave={handleVariableSave}
          onCancel={() => {
            setVariableModalVisible(false);
            setEditingVariable(null);
          }}
        />
      </Modal>
    );
  };

  // Email sequence table columns
  const emailColumns = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => (
        <Tooltip title={text}>
          {text.length > 40 ? `${text.substring(0, 40)}...` : text}
        </Tooltip>
      ),
    },
    {
      title: "Template",
      dataIndex: "templateId",
      key: "templateId",
      render: (templateId) => {
        const template = templates.find((t) => t._id === templateId);
        return template?.name || "Template not found";
      },
    },
    {
      title: "Delay",
      key: "delay",
      render: (_, record) => (
        <span>
          {record.delayAfterPrevious.value} {record.delayAfterPrevious.unit}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditEmail(record, index)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteEmail(index)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Row>
      <Col span={24}>
        {/* Template Selection Hero */}
        <Card
          style={{
            marginBottom: 24,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
            borderRadius: 12,
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Typography.Title level={4} style={{ color: "#fff", margin: 0, marginBottom: 4 }}>
                {selectedTemplateName ? `Using: ${selectedTemplateName}` : "Start with a Template"}
              </Typography.Title>
              <Typography.Text style={{ color: "rgba(255,255,255,0.85)" }}>
                {selectedTemplateName
                  ? "Review and customize the settings below, then add your emails"
                  : "Choose from 33+ pre-built sequences based on billion-dollar company best practices"
                }
              </Typography.Text>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<AppstoreOutlined />}
                onClick={() => setTemplateGalleryVisible(true)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: 8,
                  height: 44,
                  fontWeight: 500,
                }}
              >
                {selectedTemplateName ? "Change Template" : "Browse Templates"}
              </Button>
            </Col>
          </Row>
        </Card>

        <Form layout="vertical">
          <Card title="Sequence Details">
            <Form.Item
              label="Sequence Name"
              required
              tooltip="A descriptive name for this email sequence"
            >
              <Input
                placeholder="Enter sequence name"
                value={values.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Description">
              <Input.TextArea
                placeholder="Enter sequence description"
                value={values.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                rows={4}
              />
            </Form.Item>

            <Form.Item
              label="Sequence Type"
              required
              tooltip="The purpose or category of this sequence"
            >
              <Select
                value={values.type}
                onChange={(value) => handleFormChange("type", value)}
              >
                {SEQUENCE_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>

          <Divider />

          <Card title="Trigger Configuration">
            <Form.Item
              label="Trigger Event"
              required
              tooltip="The event that will start this sequence"
            >
              <Select
                value={values.trigger.eventType}
                onChange={(value) => handleTriggerChange("eventType", value)}
              >
                {TRIGGER_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Initial Delay"
              required
              tooltip="Delay before sending the first email after the trigger"
            >
              <Input.Group compact>
                <InputNumber
                  style={{ width: "60%" }}
                  min={0}
                  value={values.trigger.delay.value}
                  onChange={(value) => handleDelayChange("value", value)}
                />
                <Select
                  style={{ width: "40%" }}
                  value={values.trigger.delay.unit}
                  onChange={(value) => handleDelayChange("unit", value)}
                >
                  {TIME_UNITS.map((unit) => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </Input.Group>
            </Form.Item>
          </Card>

          <Divider />

          <Card title="Audience Segmentation (Optional)">
            <Alert
              message="Define your target audience"
              description="Leave fields as 'Any' to include all users, or specify criteria to target specific segments."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Collapse ghost>
              <Collapse.Panel 
                header={
                  <Space>
                    <FilterOutlined />
                    <span>Behavior-based Segmentation</span>
                  </Space>
                } 
                key="behavior"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Usage Pattern">
                      <Select
                        value={values.segmentation.behavior.usagePattern}
                        onChange={(value) => 
                          handleSegmentationChange("behavior", "usagePattern", value)
                        }
                      >
                        {USAGE_PATTERNS.map((pattern) => (
                          <Option key={pattern.value} value={pattern.value}>
                            {pattern.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Engagement Level">
                      <Select
                        value={values.segmentation.behavior.engagementLevel}
                        onChange={(value) => 
                          handleSegmentationChange("behavior", "engagementLevel", value)
                        }
                      >
                        {ENGAGEMENT_LEVELS.map((level) => (
                          <Option key={level.value} value={level.value}>
                            {level.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Last Activity (Days Ago)">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.lastActivityDaysAgo?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "lastActivityDaysAgo", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.lastActivityDaysAgo?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "lastActivityDaysAgo", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Total Generations">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.totalGenerations?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "totalGenerations", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.totalGenerations?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "totalGenerations", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Credits Used">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.creditsUsed?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsUsed", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.creditsUsed?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsUsed", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Credits Remaining">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.creditsRemaining?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsRemaining", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.creditsRemaining?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsRemaining", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </Collapse.Panel>
              
              <Collapse.Panel 
                header={
                  <Space>
                    <FilterOutlined />
                    <span>Demographic Segmentation</span>
                  </Space>
                } 
                key="demographic"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Days Since Signup">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.demographic.daysSinceSignup?.min}
                          onChange={(value) => 
                            handleRangeChange("demographic", "daysSinceSignup", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.demographic.daysSinceSignup?.max}
                          onChange={(value) => 
                            handleRangeChange("demographic", "daysSinceSignup", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Signup Date Range">
                      <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        value={[
                          values.segmentation.demographic.signupDateRange?.start ? 
                            moment(values.segmentation.demographic.signupDateRange.start) : null,
                          values.segmentation.demographic.signupDateRange?.end ? 
                            moment(values.segmentation.demographic.signupDateRange.end) : null,
                        ]}
                        onChange={(dates) => {
                          handleSegmentationChange("demographic", "signupDateRange", {
                            start: dates?.[0]?.toDate(),
                            end: dates?.[1]?.toDate(),
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Subscription Tier">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.subscriptionTier}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "subscriptionTier", value)
                        }
                      >
                        {SUBSCRIPTION_TIERS.map((tier) => (
                          <Option key={tier.value} value={tier.value}>
                            {tier.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Subscription Status">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.subscriptionStatus}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "subscriptionStatus", value)
                        }
                      >
                        {SUBSCRIPTION_STATUS.map((status) => (
                          <Option key={status.value} value={status.value}>
                            {status.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Account Type">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.accountType}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "accountType", value)
                        }
                      >
                        {ACCOUNT_TYPES.map((type) => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="User Role">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.userRole}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "userRole", value)
                        }
                      >
                        {USER_ROLES.map((role) => (
                          <Option key={role.value} value={role.value}>
                            {role.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
          </Card>

          <Divider />

          <Card
            title="Email Sequence"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddEmail}
              >
                Add Email
              </Button>
            }
          >
            {values.emails.length > 0 ? (
              <>
                <Alert
                  message={`${values.emails.length} email${
                    values.emails.length !== 1 ? "s" : ""
                  } in sequence`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Table
                  columns={emailColumns}
                  dataSource={values.emails}
                  rowKey={(record, index) => index}
                  pagination={false}
                />
              </>
            ) : (
              <Empty description="No emails added to sequence yet" />
            )}
          </Card>

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                Create Sequence
              </Button>
              <Button onClick={() => history.goBack()}>Cancel</Button>
            </Space>
          </Form.Item>

          <EmailForm
            email={editingEmail}
            onSave={handleSaveEmail}
            onCancel={() => setEmailModalVisible(false)}
            emailModalVisible={emailModalVisible}
          />

          {/* Sequence Template Gallery */}
          <SequenceTemplateGallery
            visible={templateGalleryVisible}
            onClose={() => setTemplateGalleryVisible(false)}
            onSelect={handleTemplateSelect}
          />

          {/* HTML Template Preview Modal */}
          <Modal
            title={templatePreviewData?.name || "Template Preview"}
            visible={templatePreviewVisible}
            onCancel={() => {
              setTemplatePreviewVisible(false);
              setTemplatePreviewData(null);
            }}
            footer={[
              <Button
                key="close"
                onClick={() => {
                  setTemplatePreviewVisible(false);
                  setTemplatePreviewData(null);
                }}
              >
                Close
              </Button>,
            ]}
            width={900}
            bodyStyle={{ padding: 0, height: "70vh" }}
            centered
            zIndex={1100}
          >
            {templatePreviewLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Spin size="large" tip="Loading preview..." />
              </div>
            ) : templatePreviewData?.compiledHtml ? (
              <iframe
                srcDoc={templatePreviewData.compiledHtml}
                title="Email Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  color: "#999",
                }}
              >
                No preview available. Template may not have compiled HTML.
              </div>
            )}
          </Modal>
        </Form>
      </Col>
    </Row>
  );
};

export default withRouter(EmailSequenceCreateForm);
